import React, { useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Sparkles, AlertTriangle, TrendingUp, Bike, UserMinus, Building2, Lightbulb, RefreshCw } from "lucide-react";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";

const ICONS = { anomaly: AlertTriangle, slab: TrendingUp, ev_risk: Bike, churn: UserMinus, bottleneck: Building2, tip: Lightbulb };
const SEV = { high: "text-rose-500 bg-rose-500/10 border-rose-500/30", medium: "text-amber-500 bg-amber-500/10 border-amber-500/30", low: "text-blue-500 bg-blue-500/10 border-blue-500/30" };

export function AIInsightsDrawer() {
  const { insightsOpen, setInsightsOpen } = useApp();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/insights");
      setData(data);
    } catch (e) { setData({ summary: "Could not load insights.", insights: [] }); }
    finally { setLoading(false); }
  };

  React.useEffect(() => { if (insightsOpen && !data) load(); }, [insightsOpen]); // eslint-disable-line

  return (
    <Sheet open={insightsOpen} onOpenChange={setInsightsOpen}>
      <SheetContent className="w-full overflow-y-auto bg-card sm:max-w-md" data-testid="ai-insights-drawer">
        <SheetHeader>
          <SheetTitle className="font-head flex items-center gap-2">
            <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-gradient-to-br from-violet-500 to-blue-500 text-white"><Sparkles className="h-4 w-4" /></span>
            AI Smart Insights
          </SheetTitle>
        </SheetHeader>
        <div className="mt-3 flex items-center justify-between">
          <p className="text-xs text-muted-foreground">Live analysis of your operations</p>
          <Button size="sm" variant="ghost" onClick={load} disabled={loading} data-testid="insights-refresh-btn">
            <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
          </Button>
        </div>

        {loading && !data && <p className="mt-8 text-center text-sm text-muted-foreground animate-pulse">Analysing operations…</p>}

        {data?.summary && (
          <div className="mt-4 rounded-xl border border-violet-500/30 bg-violet-500/5 p-4">
            <p className="text-sm font-medium leading-relaxed">{data.summary}</p>
          </div>
        )}

        <div className="mt-4 space-y-3">
          {(data?.insights || []).map((ins, i) => {
            const Icon = ICONS[ins.type] || Lightbulb;
            return (
              <div key={i} data-testid={`insight-card-${i}`} className="animate-fade-up rounded-xl border border-border bg-secondary/40 p-4" style={{ animationDelay: `${i * 60}ms` }}>
                <div className="flex items-start gap-3">
                  <div className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border ${SEV[ins.severity] || SEV.low}`}><Icon className="h-4 w-4" /></div>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold">{ins.title}</p>
                    <p className="mt-0.5 text-xs leading-relaxed text-muted-foreground">{ins.detail}</p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </SheetContent>
    </Sheet>
  );
}
